// ============================================================
//  RewardScene — collectible reveal, XP/Stars, back to Hub
// ============================================================
import { C, CS } from '../../constants/Colors.js';
import { Cosmo } from '../characters/Cosmo.js';
import { SaveManager } from '../../state/SaveManager.js';
import { audio } from '../../audio/AudioManager.js';

export class RewardScene extends Phaser.Scene {
  private cosmo!: Cosmo;

  constructor() { super({ key: 'Reward' }); }

  create() {
    SaveManager.addCollectible('number-kingdom', 'constellation-fragment-1');
    const W = this.scale.width, H = this.scale.height;

    this.cameras.main.setAlpha(0);
    this.tweens.add({ targets: this.cameras.main, alpha: 1, duration: 800 });

    this.drawBackground(W, H);
    this.createCosmo(W, H);
    this.createRewardDisplay(W, H);
    audio.celebrate();
  }

  private drawBackground(W: number, H: number) {
    const g = this.add.graphics();
    g.fillGradientStyle(0x05001a, 0x05001a, 0x0d0230, 0x0d0230, 1);
    g.fillRect(0, 0, W, H);

    // Stars
    for (let i = 0; i < 200; i++) {
      g.fillStyle(0xffffff, 0.1 + Math.random() * 0.8);
      g.fillCircle(Math.random() * W, Math.random() * H, 0.4 + Math.random() * 1.5);
    }

    // Gold nebula glow (restoration energy)
    g.fillStyle(0xff8c00, 0.06); g.fillEllipse(W * 0.5, H * 0.35, W * 0.8, H * 0.5);
    g.fillStyle(0xffd700, 0.04); g.fillEllipse(W * 0.5, H * 0.35, W * 0.5, H * 0.35);

    // Constellation lines (background)
    const stars = [
      [W * 0.2, H * 0.15], [W * 0.35, H * 0.22], [W * 0.5, H * 0.1],
      [W * 0.65, H * 0.2], [W * 0.8, H * 0.14], [W * 0.3, H * 0.3],
    ];
    g.lineStyle(1, 0x4488ff, 0.3);
    for (let i = 0; i < stars.length - 1; i++) {
      g.lineBetween(stars[i][0], stars[i][1], stars[i + 1][0], stars[i + 1][1]);
    }
    stars.forEach(([x, y]) => {
      g.fillStyle(0xffffff, 0.6 + Math.random() * 0.4); g.fillCircle(x, y, 1.5 + Math.random() * 2);
    });

    // Ambient particles
    try {
      this.add.particles(W * 0.5, H * 0.4, 'particle_star', {
        speed: { min: 20, max: 80 },
        angle: { min: 0, max: 360 },
        scale: { min: 0.3, max: 1 },
        alpha: { start: 0.8, end: 0 },
        lifespan: { min: 1500, max: 3000 },
        quantity: 1,
        frequency: 200,
        tint: [0xffd700, 0xff8c00, 0x00e5ff, 0xffffff],
      });
    } catch { /* ok */ }
  }

  private createCosmo(W: number, H: number) {
    this.cosmo = new Cosmo(this, W * 0.18, H * 0.6, 0.9);
    this.cosmo.setDepth(10);
    this.time.delayedCall(600, () => {
      this.cosmo.celebrate();
      this.cosmo.say('Amazing! You powered the Star Bridge and saved Number Kingdom! 🌟', 5000);
    });
  }

  private createRewardDisplay(W: number, H: number) {
    // Celebration header
    const headerBg = this.add.graphics().setDepth(5);
    headerBg.fillStyle(0x080018, 0.9); headerBg.fillRoundedRect(W * 0.15, H * 0.06, W * 0.7, H * 0.12, 16);
    headerBg.lineStyle(2, 0xffd700, 0.8); headerBg.strokeRoundedRect(W * 0.15, H * 0.06, W * 0.7, H * 0.12, 16);

    const congrats = this.add.text(W * 0.5, H * 0.12, '🌟 STAR BRIDGE RESTORED! 🌟', {
      fontFamily: '"Nunito", Arial, sans-serif',
      fontSize: '24px', fontStyle: 'bold',
      color: '#ffd700',
      stroke: '#000', strokeThickness: 3,
      shadow: { blur: 12, color: '#ff8c00', fill: true },
    }).setOrigin(0.5, 0.5).setDepth(6);

    // Animate congrats text scale
    this.tweens.add({ targets: congrats, scaleX: 1.06, scaleY: 1.06, duration: 700, ease: 'Sine.easeInOut', yoyo: true, repeat: -1 });

    // ── Collectible card ──────────────────────────────────────
    const cardX = W * 0.5, cardY = H * 0.36;
    const cardG = this.add.graphics().setDepth(5);
    // Outer glow
    for (let i = 6; i >= 1; i--) {
      cardG.fillStyle(0x00e5ff, 0.04 * i); cardG.fillRoundedRect(cardX - 110 - i * 5, cardY - 80 - i * 4, 220 + i * 10, 160 + i * 8, 20);
    }
    cardG.fillStyle(0x0a0530, 0.97); cardG.fillRoundedRect(cardX - 110, cardY - 80, 220, 160, 18);
    cardG.lineStyle(2, 0x00e5ff, 0.9); cardG.strokeRoundedRect(cardX - 110, cardY - 80, 220, 160, 18);
    cardG.lineStyle(1, 0x7b2fff, 0.5); cardG.strokeRoundedRect(cardX - 105, cardY - 75, 210, 150, 14);

    // "NEW!" badge
    cardG.fillStyle(0xff4500, 1); cardG.fillRoundedRect(cardX + 68, cardY - 95, 52, 22, 10);
    const newBadge = this.add.text(cardX + 94, cardY - 84, 'NEW!', {
      fontFamily: '"Nunito", Arial, sans-serif', fontSize: '11px', fontStyle: 'bold', color: '#ffffff',
    }).setOrigin(0.5, 0.5).setDepth(8);

    // Constellation fragment art
    const fragG = this.add.graphics().setDepth(7);
    this.drawConstellationFragment(fragG, cardX, cardY - 10);

    const collectLabel = this.add.text(cardX, cardY + 65, 'CONSTELLATION FRAGMENT ✦1', {
      fontFamily: '"Nunito", Arial, sans-serif', fontSize: '12px', fontStyle: 'bold', color: CS.cyan,
      stroke: '#000', strokeThickness: 2,
    }).setOrigin(0.5, 0.5).setDepth(8);

    const collectSub = this.add.text(cardX, cardY + 82, 'Number Kingdom Discovery', {
      fontFamily: '"Nunito", Arial, sans-serif', fontSize: '10px', color: '#8888aa',
    }).setOrigin(0.5, 0.5).setDepth(8);

    // Animate card entrance
    cardG.setAlpha(0); fragG.setAlpha(0); collectLabel.setAlpha(0);
    this.tweens.add({ targets: [cardG, fragG, collectLabel, collectSub, newBadge], alpha: 1, duration: 600, delay: 400 });

    // Card float
    this.tweens.add({ targets: [cardG, fragG, collectLabel, collectSub], y: '-=8', duration: 2000, ease: 'Sine.easeInOut', yoyo: true, repeat: -1, delay: 500 });

    // ── Rewards row ───────────────────────────────────────────
    const profile = SaveManager.getProfile();
    const rewardY = H * 0.65;
    const rewards = [
      { icon: '⭐', val: `+${Math.min(profile.xp, 250)} XP`, col: '#ffd700' },
      { icon: '★', val: '+3 Stars', col: '#ffd700' },
      { icon: '◆', val: `Level ${profile.level}`, col: '#80ffff' },
    ];

    const rowBg = this.add.graphics().setDepth(5);
    rowBg.fillStyle(0x080018, 0.85); rowBg.fillRoundedRect(W * 0.15, rewardY - 30, W * 0.7, 60, 12);
    rowBg.lineStyle(1, 0x3d2060, 0.7); rowBg.strokeRoundedRect(W * 0.15, rewardY - 30, W * 0.7, 60, 12);

    rewards.forEach(({ icon, val, col }, i) => {
      const rx = W * (0.25 + i * 0.25);
      this.add.text(rx, rewardY, `${icon} ${val}`, {
        fontFamily: '"Nunito", Arial, sans-serif', fontSize: '16px', fontStyle: 'bold', color: col,
        stroke: '#000', strokeThickness: 2,
      }).setOrigin(0.5, 0.5).setDepth(6).setAlpha(0);

      this.tweens.add({
        targets: this.children.list[this.children.list.length - 1] as Phaser.GameObjects.Text,
        alpha: 1, y: `-=6`,
        duration: 500, delay: 800 + i * 200,
      });
    });

    // ── World restored status ─────────────────────────────────
    const statusBg = this.add.graphics().setDepth(5);
    statusBg.fillStyle(0x001520, 0.9); statusBg.fillRoundedRect(W * 0.15, H * 0.76, W * 0.7, 40, 10);
    statusBg.lineStyle(1.5, 0x00e5ff, 0.6); statusBg.strokeRoundedRect(W * 0.15, H * 0.76, W * 0.7, 40, 10);
    this.add.text(W * 0.5, H * 0.78, '⚡ Number Kingdom World Energy: 80/100 — RESTORED!', {
      fontFamily: '"Nunito", Arial, sans-serif', fontSize: '13px', fontStyle: 'bold', color: CS.cyan,
      stroke: '#000', strokeThickness: 2,
    }).setOrigin(0.5, 0).setDepth(6);

    // ── What's next teaser ────────────────────────────────────
    const teaserBg = this.add.graphics().setDepth(5);
    teaserBg.fillStyle(0x1a0a00, 0.85); teaserBg.fillRoundedRect(W * 0.15, H * 0.83, W * 0.7, 36, 10);
    teaserBg.lineStyle(1, 0xff8c00, 0.5); teaserBg.strokeRoundedRect(W * 0.15, H * 0.83, W * 0.7, 36, 10);
    this.add.text(W * 0.5, H * 0.851, '🔮 The ancient number tower is awakening… more mysteries await!', {
      fontFamily: '"Nunito", Arial, sans-serif', fontSize: '12px', color: '#ffcc88',
    }).setOrigin(0.5, 0).setDepth(6);

    // ── Continue button ───────────────────────────────────────
    this.time.delayedCall(1000, () => this.createContinueButton(W, H));
  }

  private drawConstellationFragment(g: Phaser.GameObjects.Graphics, cx: number, cy: number) {
    // Glowing constellation pattern — a mini star map
    const nodes = [
      [0, -45], [-32, -20], [32, -20], [-18, 12], [18, 12], [0, 32],
    ] as const;
    const connections = [[0,1],[0,2],[1,3],[2,4],[3,5],[4,5],[1,4],[2,3]];

    // Connection lines
    connections.forEach(([a, b]) => {
      const [ax, ay] = nodes[a], [bx, by] = nodes[b];
      for (let i = 3; i >= 1; i--) {
        g.lineStyle(i, 0x00e5ff, 0.06 * i);
        g.lineBetween(cx + ax, cy + ay, cx + bx, cy + by);
      }
      g.lineStyle(1.5, 0x4488ff, 0.7);
      g.lineBetween(cx + ax, cy + ay, cx + bx, cy + by);
    });

    // Star nodes
    nodes.forEach(([nx, ny], i) => {
      const isPrimary = i === 0;
      const r = isPrimary ? 8 : 5;
      const col = isPrimary ? 0xffd700 : 0x00e5ff;
      for (let ri = 4; ri >= 1; ri--) {
        g.fillStyle(col, 0.06 * ri); g.fillCircle(cx + nx, cy + ny, r + ri * 5);
      }
      g.fillStyle(col, 1); g.fillCircle(cx + nx, cy + ny, r);
      g.fillStyle(0xffffff, isPrimary ? 0.7 : 0.5); g.fillCircle(cx + nx - r * 0.3, cy + ny - r * 0.3, r * 0.35);
    });

    // Fragment border (hexagonal shape)
    const hexPts: { x: number; y: number }[] = [];
    for (let i = 0; i < 6; i++) {
      const a = (i * Math.PI) / 3 - Math.PI / 6;
      hexPts.push({ x: cx + Math.cos(a) * 60, y: cy + Math.sin(a) * 60 });
    }
    g.lineStyle(1.5, 0x00e5ff, 0.4);
    g.strokePoints(hexPts, true);
    g.lineStyle(1, 0x4488ff, 0.2);
    const hexPts2: { x: number; y: number }[] = hexPts.map(p => ({ x: cx + (p.x - cx) * 0.85, y: cy + (p.y - cy) * 0.85 }));
    g.strokePoints(hexPts2, true);

    // Pulse animation
    this.tweens.add({
      targets: g,
      alpha: 0.7,
      duration: 1500,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });
  }

  private createContinueButton(W: number, H: number) {
    const btnG = this.add.graphics().setDepth(20);
    const btnY = H * 0.92;
    btnG.fillStyle(0xff4500, 1); btnG.fillRoundedRect(W * 0.5 - 120, btnY - 26, 240, 52, 22);
    btnG.fillStyle(0xff8c00, 1); btnG.fillRoundedRect(W * 0.5 - 118, btnY - 24, 236, 26, 18);
    btnG.fillStyle(0xffa500, 0.5); btnG.fillRoundedRect(W * 0.5 - 110, btnY - 21, 150, 15, 10);
    for (let i = 5; i >= 1; i--) {
      btnG.fillStyle(0xff6b00, 0.04 * i);
      btnG.fillRoundedRect(W * 0.5 - 120 - i * 4, btnY - 26 - i * 3, 240 + i * 8, 52 + i * 6, 24);
    }

    const btnText = this.add.text(W * 0.5, btnY, '✨ Back to the Universe!', {
      fontFamily: '"Nunito", Arial, sans-serif',
      fontSize: '18px', fontStyle: 'bold', color: '#ffffff',
      stroke: '#cc3300', strokeThickness: 3,
    }).setOrigin(0.5, 0.5).setDepth(21);

    btnG.setAlpha(0); btnText.setAlpha(0);
    this.tweens.add({ targets: [btnG, btnText], alpha: 1, duration: 500 });

    btnG.setInteractive(new Phaser.Geom.Rectangle(W * 0.5 - 120, btnY - 26, 240, 52), Phaser.Geom.Rectangle.Contains);
    btnG.on('pointerover', () => {
      this.tweens.add({ targets: [btnG, btnText], scaleX: 1.04, scaleY: 1.04, duration: 120 });
      audio.click();
    });
    btnG.on('pointerout', () => this.tweens.add({ targets: [btnG, btnText], scaleX: 1, scaleY: 1, duration: 120 }));
    btnG.on('pointerdown', () => {
      audio.unlock();
      this.cameras.main.fade(500, 0, 0, 20);
      this.time.delayedCall(520, () => this.scene.start('HomeHub'));
    });
  }
}
